/**
 * TES-2229 S8 (LH §L5.10, Mig 399) — Selbstkorrektur-Entscheidungskette eines Mangels.
 *
 * Liest rpc_defect_selfcorrect_status: die aufgelöste 4-stufige Kette
 * (Provenienz → Anker-Spiegel → Kontrakt-Fenster → Bedingung) + Verdict
 * (may_cancel / may_soft_delete / may_suggest_cancel / may_reopen).
 * SERVER-SoT: dieselben Prädikate wie rpc_cancel_defect / guard_defect_soft_delete
 * (US1-REFACTOR: eine Fensterlogik, UI liest dasselbe Prädikat). Die UI zeigt das
 * Verdict transparent an — sie ist KEINE Sicherheitsgrenze (RLS/Guards sind es).
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface DefectSelfcorrectStatus {
  is_creator: boolean;
  anchor_mirror: {
    task_links: boolean;
    sap: boolean;
    status_locked: boolean;
  };
  window: {
    status: string;
    status_open: boolean;
    foreign_activity_at: string | null;
    assigned_to_third_party: boolean;
    open: boolean;
  };
  may_cancel: boolean;
  may_cancel_path: "creator_window" | "qm_role" | "verifier_or_deputy" | null;
  may_soft_delete: boolean;
  may_suggest_cancel: boolean;
  may_reopen: boolean;
}

type SelfcorrectRpc = {
  rpc: (
    fn: "rpc_defect_selfcorrect_status",
    args: { _defect_id: string },
  ) => Promise<{
    data: DefectSelfcorrectStatus | null;
    error: { code?: string; message: string } | null;
  }>;
};

export const defectSelfcorrectKeys = {
  forDefect: (defectId: string) => ["defect-selfcorrect", defectId] as const,
};

async function fetchSelfcorrectStatus(defectId: string): Promise<DefectSelfcorrectStatus> {
  const { data, error } = await (supabase as unknown as SelfcorrectRpc).rpc(
    "rpc_defect_selfcorrect_status",
    { _defect_id: defectId },
  );
  if (error) throw error;
  if (!data) throw new Error("empty selfcorrect verdict");
  return data;
}

export function useDefectSelfcorrect(defectId: string | undefined) {
  const { session } = useAuth();

  return useQuery({
    queryKey: defectSelfcorrectKeys.forDefect(defectId ?? ""),
    queryFn: () => fetchSelfcorrectStatus(defectId!),
    enabled: !!defectId && !!session,
    staleTime: 15_000,
  });
}
